/**
 * Nutrition Calculator Utilities
 * Extracted from NutritionTracker.js for better testability
 */

/**
 * Calculate BMR using Mifflin-St Jeor Equation
 * @param {number} weightKg - Weight in kilograms
 * @param {number} heightCm - Height in centimeters
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {number} BMR in calories
 */
export function calculateBMR(weightKg, heightCm, age, gender) {
  if (weightKg <= 0 || heightCm <= 0 || age <= 0) {
    return 0;
  }
  
  if (gender === 'male') {
    return Math.round((10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5);
  } else {
    return Math.round((10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161);
  }
}

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 * @param {number} bmr - Basal Metabolic Rate
 * @param {number} activityFactor - Activity multiplier (1.2 sedentary, 1.375 light, 1.55 moderate, 1.725 active, 1.9 very active)
 * @returns {number} TDEE in calories
 */
export function calculateTDEE(bmr, activityFactor = 1.2) {
  return Math.round(bmr * activityFactor);
}

/**
 * Convert weight between units
 * @param {number} weight - Weight value
 * @param {string} fromUnit - Source unit ('kg' or 'lbs')
 * @param {string} toUnit - Target unit ('kg' or 'lbs')
 * @returns {number} Converted weight
 */
export function convertWeight(weight, fromUnit, toUnit) {
  if (fromUnit === toUnit) return weight;
  
  if (fromUnit === 'lbs' && toUnit === 'kg') {
    return weight * 0.453592;
  } else if (fromUnit === 'kg' && toUnit === 'lbs') {
    return weight / 0.453592;
  }
  return weight;
}

/**
 * Convert height between units
 * @param {number} height - Height value
 * @param {string} fromUnit - Source unit ('cm' or 'in')
 * @param {string} toUnit - Target unit ('cm' or 'in')
 * @returns {number} Converted height
 */
export function convertHeight(height, fromUnit, toUnit) {
  if (fromUnit === toUnit) return height;
  
  if (fromUnit === 'in' && toUnit === 'cm') {
    return height * 2.54;
  } else if (fromUnit === 'cm' && toUnit === 'in') {
    return height / 2.54;
  }
  return height;
}

/**
 * Calculate weight goal calorie adjustment
 * @param {string} goal - 'lose' or 'maintain'
 * @param {number} weekNumber - Current week in the program
 * @param {boolean} isRelaxDay - Whether it's a relax day (Sunday for weight loss)
 * @returns {number} Calorie adjustment (negative for deficit, 0 for maintain)
 */
export function calculateGoalAdjustment(goal, weekNumber, isRelaxDay = false) {
  if (goal === 'maintain' || weekNumber <= 0) {
    return 0;
  }
  
  if (goal === 'lose') {
    // Cycling: 12 weeks lose, 8 weeks maintain (2 months), then repeat
    const cycleLength = 20; // 12 lose + 8 maintain
    const cycleWeek = ((weekNumber - 1) % cycleLength) + 1;
    
    // Weeks 13-20 in cycle = maintain phase
    if (cycleWeek > 12) {
      return 0;
    }
    
    // Lose phase:
    // Week 1-2: gentle start -250 kcal
    // Week 3-10: full deficit -500 kcal (Sunday relax: -250)
    // Week 11-12: taper back -250 kcal (reverse diet)
    if (cycleWeek <= 2) {
      return -250;
    } else if (cycleWeek <= 10) {
      return isRelaxDay ? -250 : -500;
    } else {
      return -250;
    }
  }
  
  return 0;
}

/**
 * Calculate macro nutrients from portion size
 * @param {object} per100g - Nutrition per 100g { calories, protein, carbs, fat }
 * @param {number} portionGrams - Portion size in grams
 * @returns {object} Calculated macros for portion
 */
export function calculatePortionMacros(per100g, portionGrams) {
  const multiplier = portionGrams / 100;
  return {
    calories: Math.round(per100g.calories * multiplier),
    protein: Math.round(per100g.protein * multiplier * 10) / 10,
    carbs: Math.round(per100g.carbs * multiplier * 10) / 10,
    fat: Math.round(per100g.fat * multiplier * 10) / 10
  };
}

/**
 * Calculate total macros from food entries
 * @param {array} entries - Array of food entries
 * @returns {object} Total macros { calories, protein, carbs, fat }
 */
export function calculateTotalMacros(entries) {
  return entries.reduce((totals, entry) => ({
    calories: totals.calories + (entry.calories || 0),
    protein: totals.protein + (entry.protein || 0),
    carbs: totals.carbs + (entry.carbs || 0),
    fat: totals.fat + (entry.fat || 0)
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
}

/**
 * Calculate workout calories burned (estimate)
 * @param {number} durationMinutes - Workout duration in minutes
 * @param {string} intensity - 'low', 'medium', or 'high'
 * @returns {number} Estimated calories burned
 */
export function calculateWorkoutCalories(durationMinutes, intensity = 'medium') {
  const caloriesPerHour = {
    low: 250,
    medium: 350,
    high: 500
  };
  
  const rate = caloriesPerHour[intensity] || caloriesPerHour.medium;
  return Math.round((durationMinutes / 60) * rate);
}

/**
 * Calculate walking calories burned
 * @param {number} steps - Number of steps
 * @param {number} strideLength - Stride length in meters
 * @param {number} weightKg - Body weight in kg
 * @returns {number} Estimated calories burned
 */
export function calculateWalkingCalories(steps, strideLength = 0.75, weightKg = 70) {
  // Distance in km
  const distanceKm = (steps * strideLength) / 1000;
  // Rough estimate: 0.5 kcal per kg per km walking
  return Math.round(distanceKm * weightKg * 0.5);
}

/**
 * Format date to locale string
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} locale - Locale string (default: 'en-US')
 * @returns {string} Formatted date string
 */
export function formatDate(dateStr, locale = 'en-US') {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Get week number from goal start date
 * @param {string} startDateStr - Start date string
 * @param {string} currentDateStr - Current date string
 * @returns {number} Week number (1-indexed)
 */
export function getGoalWeekNumber(startDateStr, currentDateStr) {
  const startDate = new Date(startDateStr);
  const currentDate = new Date(currentDateStr);
  const daysDiff = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
  return Math.floor(daysDiff / 7) + 1;
}

export default {
  calculateBMR,
  calculateTDEE,
  convertWeight,
  convertHeight,
  calculateGoalAdjustment,
  calculatePortionMacros,
  calculateTotalMacros,
  calculateWorkoutCalories,
  calculateWalkingCalories,
  formatDate,
  getGoalWeekNumber
};
